import * as XLSX from 'xlsx'

/**
 * Excel Export Utilities for Adherence Reports
 * Provides functions for downloading adherence report data as Excel files
 */

/**
 * Downloads adherence report data as an Excel file with multiple sheets.
 * Exclusion status is read from each row's `excluded` field in dayLevelData
 * so the export always matches the persisted data.
 * @param {Object} params - Export parameters
 * @param {Array} params.dayLevelData - Daily breakdown data (single source of truth)
 * @param {Object} params.subjectInfo - Subject information (subid, dataset_identifier)
 * @param {Object} params.thresholds - Current adherence thresholds
 * @param {Object} params.summaryStats - Summary statistics from computed properties
 * @returns {Promise} - Download promise
 */
export async function downloadAdherenceReport({ 
  dayLevelData = [], 
  subjectInfo = {}, 
  thresholds = {}, 
  summaryStats = {}
}) {
  try {
    // Create a new workbook
    const workbook = XLSX.utils.book_new()

    // Sheet 1: Daily Breakdown Data
    const dailyDataArray = createDailyBreakdownSheet(dayLevelData, thresholds)
    const dailySheet = XLSX.utils.aoa_to_sheet(dailyDataArray)
    XLSX.utils.book_append_sheet(workbook, dailySheet, 'Daily Breakdown')

    // Sheet 2: Summary Statistics
    const summaryDataArray = createSummaryStatsSheet(summaryStats, subjectInfo)
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryDataArray)
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary Statistics')

    // Sheet 3: Adherence Thresholds
    const thresholdsDataArray = createThresholdsSheet(thresholds, subjectInfo)
    const thresholdsSheet = XLSX.utils.aoa_to_sheet(thresholdsDataArray)
    XLSX.utils.book_append_sheet(workbook, thresholdsSheet, 'Adherence Thresholds')

    // Generate filename
    const subid = subjectInfo.subid || 'Unknown'
    const datasetId = subjectInfo.dataset_identifier || '001'
    const timestamp = new Date().toISOString().slice(0, 10) // YYYY-MM-DD
    const filename = `${subid}_${datasetId}_adherence_report_${timestamp}.xlsx`

    // Write and download the file
    XLSX.writeFile(workbook, filename)

    return { success: true, filename }
  } catch (error) {
    console.error('Adherence report export failed:', error)
    throw error
  }
}

/**
 * Creates the daily breakdown data sheet.
 * Exclusion is read from each row's `excluded` field — no separate map needed.
 * @param {Array} dayLevelData - Daily data array (single source of truth)
 * @param {Object} thresholds - Adherence thresholds for calculations
 * @returns {Array} - Worksheet data array
 */
function createDailyBreakdownSheet(dayLevelData, thresholds = {}) {
  const headers = [
    'Day',
    'Date',
    'Start Time',
    'End Time',
    'Powered On (Hours)',
    'Powered On (%)',
    'Worn (Hours)',
    'Worn (%)',
    'Extreme Negative (Hours)',
    'Extreme Negative (%)',
    'Adherence Status',
    'Biosensor Health Status',
    'Excluded',
    'Temperature Mean',
    'Temperature Max',
    'Temperature Min',
    'Temperature SD',
    'Motion Mean',
    'Motion Max',
    'Motion Min',
    'Motion SD',
    'Gap Duration',
    'Non-Wear Duration',
    'Jump Duration',
    'Plummet Duration',
    'Low Quality Duration'
  ]

  const data = [headers]

  dayLevelData.forEach((day, index) => {
    const isExcluded = !!day.excluded
    
    // Debug: Log percentage values for first day to diagnose export issue
    if (index === 0) {
      console.log('🔍 Export debug - First day percentage values:', {
        device_turned_on_percent_of_day: day.device_turned_on_percent_of_day,
        device_turned_on_percent_of_day_type: typeof day.device_turned_on_percent_of_day,
        device_turned_on_percent_of_day_calc: Number(day.device_turned_on_percent_of_day || 0) * 100,
        device_worn_percent_of_day: day.device_worn_percent_of_day,
        device_worn_percent_of_day_type: typeof day.device_worn_percent_of_day,
        device_worn_percent_of_day_calc: Number(day.device_worn_percent_of_day || 0) * 100,
        extreme_negative_percent_of_day: day.extreme_negative_percent_of_day,
        extreme_negative_percent_of_day_type: typeof day.extreme_negative_percent_of_day,
        extreme_negative_percent_of_day_calc: Number(day.extreme_negative_percent_of_day || 0) * 100,
        allKeys: Object.keys(day).filter(k => k.includes('percent') || k.includes('duration'))
      })
    }
    
    // Calculate adherence status (1 = adherent, 0 = not adherent); blank when excluded
    const wornHours = Number(day.device_worn_duration || 0)
    const isAdherent = isExcluded ? '' : (wornHours >= (thresholds.deviceWornHours || 0) ? 1 : 0)
    
    // Calculate biosensor health status (1 = healthy, 0 = unhealthy); blank when excluded
    const extremeNegHours = Number(day.extreme_negative_duration || 0)
    const isHealthy = isExcluded ? '' : (extremeNegHours <= (thresholds.extremeNegativeMaxHours || 0) ? 1 : 0)
    
    const row = [
      index + 1, // Day number
      formatDateForExport(day.date),
      formatTimeForExport(day.begin_day),
      formatTimeForExport(day.end_day),
      Number(day.device_turned_on_duration || 0).toFixed(1),
      (Number(day.device_turned_on_percent_of_day || 0) * 100).toFixed(1),
      Number(day.device_worn_duration || 0).toFixed(1),
      (Number(day.device_worn_percent_of_day || 0) * 100).toFixed(1),
      Number(day.extreme_negative_duration || 0).toFixed(1),
      (Number(day.extreme_negative_percent_of_day || 0) * 100).toFixed(1),
      isAdherent, // Adherence status: 1 = adherent, 0 = not adherent
      isHealthy, // Biosensor health status: 1 = healthy, 0 = unhealthy
      isExcluded ? 'Yes' : 'No',
      Number(day.temp_mean || 0).toFixed(2),
      Number(day.temp_max || 0).toFixed(2),
      Number(day.temp_min || 0).toFixed(2),
      Number(day.temp_sd || 0).toFixed(2),
      Number(day.motion_mean || 0).toFixed(2),
      Number(day.motion_max || 0).toFixed(2),
      Number(day.motion_min || 0).toFixed(2),
      Number(day.motion_sd || 0).toFixed(2),
      Number(day.gap_duration || 0).toFixed(1),
      Number(day.non_wear_duration || 0).toFixed(1),
      Number(day.jump_duration || 0).toFixed(1),
      Number(day.plummet_duration || 0).toFixed(1),
      Number(day.low_quality_duration || 0).toFixed(1)
    ]
    
    data.push(row)
  })

  return data
}

/**
 * Creates the summary statistics sheet
 * @param {Object} summaryStats - Summary statistics object
 * @param {Object} subjectInfo - Subject information
 * @returns {Array} - Worksheet data array
 */
function createSummaryStatsSheet(summaryStats, subjectInfo) {
  const data = [
    ['Adherence & Biosensor Health Summary Report'],
    [''],
    ['Subject Information:'],
    ['Subject ID', subjectInfo.subid || 'N/A'],
    ['Dataset', subjectInfo.dataset_identifier || 'N/A'],
    ['Total Days', summaryStats.totalDays || 0],
    ['Used Days', summaryStats.usedDays || 0],
    [''],
    ['Summary Statistics:'],
    ['Metric', 'Average Percentage', 'Average Hours/Day'],
    ['Device Powered On', `${summaryStats.averageDeviceTurnedOn || 0}%`, `${summaryStats.averageDeviceTurnedOnHours || 0}h`],
    ['Device Worn', `${summaryStats.averageDeviceWorn || 0}%`, `${summaryStats.averageDeviceWornHours || 0}h`],
    ['Jumps', `${summaryStats.averageJumpsPct || 0}%`, `${summaryStats.averageJumpsHours || 0}h`],
    ['Plummets', `${summaryStats.averagePlummetsPct || 0}%`, `${summaryStats.averagePlummetsHours || 0}h`],
    ['Extreme Negative', `${summaryStats.averageExtremeNegativePct || 0}%`, `${summaryStats.averageExtremeNegativeHours || 0}h`],
    [''],
    ['Issue Summary:'],
    ['Metric', 'Count', 'Percentage'],
    ['Adherent & Healthy Days', summaryStats.adherentAndHealthyDaysCount || 0, `${summaryStats.adherentAndHealthyPercentage || 0}%`],
    ['Non-adherent Days', summaryStats.nonAdherentDaysCount || 0, `${summaryStats.nonAdherentPercentage || 0}%`],
    ['Low Health Days', summaryStats.lowHealthDaysCount || 0, `${summaryStats.lowHealthPercentage || 0}%`],
    ['Excluded Days', summaryStats.excludedDaysCount || 0, `${summaryStats.excludedPercentage || 0}%`],
    [''],
    ['Temperature Statistics:'],
    ['Metric', 'Value'],
    ['Average Temperature', `${summaryStats.averageTemperature || 0}°C`],
    ['Temperature Range', `${summaryStats.temperatureRange || 0}°C`],
    ['Average Temperature SD', `${summaryStats.averageTemperatureSD || 0}°C`],
    [''],
    ['Motion Statistics:'],
    ['Metric', 'Value'],
    ['Average Motion', summaryStats.averageMotion || 0],
    ['Motion Range', summaryStats.motionRange || 0],
    ['Average Motion SD', summaryStats.averageMotionSD || 0],
    [''],
    ['Report Generated:', new Date().toLocaleString()]
  ]

  return data
}

/**
 * Creates the adherence thresholds sheet
 * @param {Object} thresholds - Current thresholds
 * @param {Object} subjectInfo - Subject information
 * @returns {Array} - Worksheet data array
 */
function createThresholdsSheet(thresholds, subjectInfo) {
  const data = [
    ['Adherence Thresholds Configuration'],
    [''],
    ['Subject Information:'],
    ['Subject ID', subjectInfo.subid || 'N/A'],
    ['Dataset', subjectInfo.dataset_identifier || 'N/A'],
    [''],
    ['Biosensor Adherence Thresholds:'],
    ['Threshold Type', 'Value', 'Description'],
    ['Device Worn (Minimum)', `${thresholds.deviceWornHours || 0} hours`, 'Minimum worn hours per day for adherence; also used to highlight Powered On and Worn columns'],
    [''],
    ['Biosensor Health Thresholds:'],
    ['Threshold Type', 'Value', 'Description'],
    ['Extreme Negative TAC (Maximum)', `${thresholds.extremeNegativeMaxHours || 0} hours`, 'Maximum hours of extreme negative TAC allowed per day'],
    [''],
    ['Threshold Definitions:'],
    [''],
    ['Adherence Status:'],
    ['- A day is considered ADHERENT if device worn hours meet or exceed the Device Worn minimum threshold'],
    ['- A day is considered NOT ADHERENT if device worn hours fall below the Device Worn minimum threshold'],
    [''],
    ['Biosensor Health Status:'],
    ['- A day is considered HEALTHY if extreme negative TAC duration is at or below the maximum threshold'],
    ['- A day is considered UNHEALTHY if extreme negative TAC duration exceeds the maximum threshold'],
    [''],
    ['Configuration Notes:'],
    ['- Thresholds can be adjusted in the application interface'],
    ['- Changes to thresholds will affect adherence and health calculations'],
    ['- Excluded days are not included in summary statistics'],
    [''],
    ['Report Generated:', new Date().toLocaleString()]
  ]

  return data
}

/**
 * Formats date string for export (more readable format)
 * @param {string} dateString - Date string to format
 * @returns {string} - Formatted date string
 */
function formatDateForExport(dateString) {
  if (!dateString) return 'N/A'
  try {
    // Handle date-only strings (YYYY-MM-DD) by appending time component
    // JavaScript's Date() interprets date-only strings as UTC, causing timezone shifts
    // By adding 'T00:00:00', we ensure it's parsed as local time
    let dateInput = dateString
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      // Date-only string (YYYY-MM-DD) - append time to parse as local time
      dateInput = dateString + 'T00:00:00'
    }
    const date = new Date(dateInput)
    if (isNaN(date.getTime())) return dateString
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    })
  } catch (error) {
    return dateString
  }
}

/**
 * Formats datetime string for export (shows date and time)
 * @param {string} dateTimeString - DateTime string to format (e.g., begin_day or end_day)
 * @returns {string} - Formatted datetime string (MM/DD/YYYY HH:MM AM/PM)
 */
function formatTimeForExport(dateTimeString) {
  if (!dateTimeString) return 'N/A'
  try {
    const date = new Date(dateTimeString)
    if (isNaN(date.getTime())) return 'N/A'
    
    // Format as MM/DD/YYYY HH:MM AM/PM
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()
    
    // Format hour in 12-hour format
    let hour = date.getHours()
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const ampm = hour >= 12 ? 'PM' : 'AM'
    hour = hour % 12
    hour = hour === 0 ? 12 : hour // 0 should be 12
    
    return `${month}/${day}/${year} ${hour}:${minutes} ${ampm}`
  } catch (error) {
    return dateTimeString || 'N/A'
  }
}

/**
 * Validates that required data is available for export
 * @param {Object} params - Export parameters
 * @returns {boolean} - True if data is valid for export
 */
export function validateAdherenceReportData(params) {
  const { dayLevelData = [], subjectInfo = {} } = params
  
  if (!dayLevelData || dayLevelData.length === 0) {
    console.warn('No daily data available for export')
    return false
  }
  
  if (!subjectInfo.subid) {
    console.warn('No subject ID available for export')
    return false
  }
  
  return true
}
